import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axios from "axios";
import './ESICForm.css';
import RegistrationNavbar from "../../EmployeesProfile/PendingProfiles/EmployeeCreation/EmployeesRegistrationNavbar/EmployeeRegistartion/RegistrationNavbar";
import DatePicker from 'react-datepicker';
import { capitalizeFirstLetter } from "../../../utils/validations";

const ESICForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const employeeUniqueId = searchParams.get('employeeUniqueId') || location.state?.applicationId;

  const [personalData, setPersonalData] = useState({
    id: "",
    surName: "",
    employeeName: "",
    guardianName: "",
    relationship: "",
    nameAsAadhar: "",
    gender: "",
    dateOfBirth: "",
    aadhaarNumber: "",
    contactNumber: "",
    emailAddress: "",
    photoUrl: "",
    photoFile: null,
    addressId: null, // Store the address ID from API
    addresses: {
      presentAddress: {
        line1: "",
        line2: "",
        block: "",
        district: "",
        state: "",
        country: "",
        postcode: ""
      },
      permanentAddress: {
        line1: "",
        line2: "",
        block: "",
        district: "",
        state: "",
        country: "",
        postcode: ""
      }
    },
    nomineeDetails: {
      nomineeName: "",
      relationship: "",
      stayingWithInsured: ""
    },
    nomineeAddress: ""
  });

  const [bankData, setBankData] = useState({
    id: null, // Store bankDetails ID from API
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: ""
  });

  const [errors, setErrors] = useState({
    surName: '',
    employeeName: '',
    guardianName: '',
    relationship: '',
    gender: '',
    dateOfBirth: '',
    aadhaarNumber: '',
    contactNumber: '',
    emailAddress: '',
    permanentAddress: '',
    presentAddress: '',
    nomineeName: '',
    nomineeRelationship: '',
    nomineeAddress: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    employeePhoto: ''
  });

  const [relationship, setRelationship] = useState([]);
  const [genders, setGenders] = useState([]);
  const [nomineeStaying, setNomineeStaying] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFocused, setIsFocused] = useState({
    gender: false,
    relationship: false,
    dateOfBirth: false,
    nomineeRelationship: false
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch personal info based on employeeUniqueId
  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/personal-info/${employeeUniqueId}`);
        const data = response.data;
        const address = data.addresses[0] || {};
        const employeeIDDetails = data.employeeIDDetails || {};
        const bank = data.bankDetails || {};

        setPersonalData(prev => ({
          ...prev,
          id: data.id || prev.id,
          surName: data.surname || prev.surName,
          employeeName: data.givenName || prev.employeeName,
          guardianName: data.guardianName || prev.guardianName,
          relationship: data.relationship?.id ? Number(data.relationship.id) : prev.relationship,
          nameAsAadhar: employeeIDDetails.nameAsPerAadhaar || prev.nameAsAadhar,
          gender: data.gender?.id ? Number(data.gender.id) : prev.gender,
          dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
          aadhaarNumber: employeeIDDetails.aadhaarNumber || prev.aadhaarNumber,
          contactNumber: data.contactNumber?.slice(0, 10) || prev.contactNumber,
          emailAddress: data.email || prev.emailAddress,
          photoUrl: employeeIDDetails.employeepassportSizeFileName || prev.photoUrl,
          photoFile: null,
          addressId: address.id || prev.addressId, // Set address ID
          addresses: {
            presentAddress: {
              line1: address.presentAddressLine1 || prev.addresses.presentAddress.line1,
              line2: address.presentAddressLine2 || prev.addresses.presentAddress.line2,
              block: address.presentBlock || prev.addresses.presentAddress.block,
              district: address.presentDistrict?.districtName || prev.addresses.presentAddress.district,
              state: address.presentState?.stateName || prev.addresses.presentAddress.state,
              country: address.presentCountry?.nationalityName || prev.addresses.presentAddress.country,
              postcode: address.presentPostCode || prev.addresses.presentAddress.postcode
            },
            permanentAddress: {
              line1: address.permanentAddressLine1 || prev.addresses.permanentAddress.line1,
              line2: address.permanentAddressLine2 || prev.addresses.permanentAddress.line2,
              block: address.permanentBlock || prev.addresses.permanentAddress.block,
              district: address.permanentDistrict?.districtName || prev.addresses.permanentAddress.district,
              state: address.permanentState?.stateName || prev.addresses.permanentAddress.state,
              country: address.permanentCountry?.nationalityName || prev.addresses.permanentAddress.country,
              postcode: address.permanentPostCode || prev.addresses.permanentAddress.postcode
            }
          },
          nomineeDetails: {
            nomineeName: employeeIDDetails.nomineeName || prev.nomineeDetails.nomineeName,
            relationship: employeeIDDetails.nomineeRelationship?.id ? Number(employeeIDDetails.nomineeRelationship.id) : prev.nomineeDetails.relationship,
            stayingWithInsured: employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no"
          },
          nomineeAddress: employeeIDDetails.nomineePresentAddress || prev.nomineeAddress
        }));
        setBankData({
          id: bank.id || null, // Set bankDetails ID
          accountHolderName: bank.accountHolderName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          branchName: bank.branchName || ""
        });
        setNomineeStaying(employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no");
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error fetching personal info:', error);
      }
    };
    if (employeeUniqueId) {
      fetchPersonalInfo();
    }
  }, [employeeUniqueId]);

  // Load localStorage on component mount
  useEffect(() => {
    const savedPersonalData = localStorage.getItem("personalData");
    const savedBankData = localStorage.getItem("bankData");
    if (savedPersonalData && !employeeUniqueId) {
      const parsedPersonalData = JSON.parse(savedPersonalData);
      setPersonalData({
        ...personalData,
        ...parsedPersonalData,
        relationship: parsedPersonalData.relationship ? Number(parsedPersonalData.relationship) : '',
        gender: parsedPersonalData.gender ? Number(parsedPersonalData.gender) : '',
        photoUrl: parsedPersonalData.photoUrl || "",
        photoFile: null,
        addressId: parsedPersonalData.addressId || null,
        addresses: parsedPersonalData.addresses || {
          presentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          },
          permanentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          }
        },
        nomineeDetails: parsedPersonalData.nomineeDetails || {
          nomineeName: "",
          relationship: "",
          stayingWithInsured: ""
        },
        nomineeAddress: parsedPersonalData.nomineeAddress || ""
      });
      setNomineeStaying(parsedPersonalData.nomineeDetails?.stayingWithInsured || "");
    }
    if (savedBankData && !employeeUniqueId) {
      const parsedBankData = JSON.parse(savedBankData);
      setBankData({
        id: parsedBankData.id || null,
        accountHolderName: parsedBankData.accountHolderName || "",
        accountNumber: parsedBankData.accountNumber || "",
        ifscCode: parsedBankData.ifscCode || "",
        branchName: parsedBankData.branchName || ""
      });
    }
    setIsDataLoaded(true);
  }, []);

  // Save personalData and bankData to localStorage on every change
  useEffect(() => {
    if (isDataLoaded) {
      const personalDataToSave = { ...personalData, photoFile: null };
      localStorage.setItem('personalData', JSON.stringify(personalDataToSave));
      localStorage.setItem('bankData', JSON.stringify(bankData));
      console.log('Saved to localStorage:', { personalData: personalDataToSave, bankData });
    }
  }, [personalData, bankData, isDataLoaded]);

  // Validate form
  useEffect(() => {
    const {
      surName = '',
      employeeName = '',
      guardianName = '',
      dateOfBirth = '',
      contactNumber = '',
      emailAddress = '',
      addresses = {},
      nomineeDetails = {},
      nomineeAddress = ''
    } = personalData || {};

    const {
      accountHolderName = '',
      accountNumber = '',
      ifscCode = ''
    } = bankData || {};

    const isValid =
      surName.trim() !== '' &&
      employeeName.trim() !== '' &&
      guardianName.trim() !== '' &&
      dateOfBirth.trim() !== '' &&
      /^[0-9]{10}$/.test(contactNumber) &&
      emailAddress.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress) &&
      addresses.permanentAddress?.line1?.trim() !== '' &&
      addresses.presentAddress?.line1?.trim() !== '' &&
      nomineeDetails.nomineeName?.trim() !== '' &&
      // nomineeAddress.trim() !== '' &&
      accountHolderName.trim() !== '' &&
      /^[0-9]{9,15}$/.test(accountNumber) &&
      /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode);

    setErrors(prev => ({
      ...prev,
      nomineeAddress: nomineeAddress.trim() === '' ? 'Nominee address is required' : ''
    }));

    console.log('Validation Check:', {
      surName,
      employeeName,
      guardianName,
      dateOfBirth,
      contactNumber,
      emailAddress,
      permanentAddressLine1: addresses.permanentAddress?.line1,
      presentAddressLine1: addresses.presentAddress?.line1,
      nomineeName: nomineeDetails.nomineeName,
      nomineeRelationship: nomineeDetails.relationship,
      nomineeAddress,
      accountHolderName,
      accountNumber,
      ifscCode,
      isValid
    });

    setIsFormValid(isValid);
  }, [personalData, bankData]);

  // Fetch relationships
  useEffect(() => {
    const fetchRelationships = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/relationships`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setRelationship(data.map(rel => ({
            value: rel.id,
            label: rel.relation
          })));
          console.log('Relationships:', data);
        } else {
          console.error('Invalid response format: relationships should be an array');
        }
      } catch (error) {
        console.error('Error fetching relationships:', error);
      }
    };
    fetchRelationships();
  }, []);

  // Fetch genders
  useEffect(() => {
    const fetchGenders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/genders`);
        const data = response.data;
        if (Array.isArray(data)) {
          setGenders(data.map(item => ({
            value: item.id,
            label: item.gender
          })));
          console.log('Genders:', data);
        } else {
          console.error('Invalid response format: genders should be an array');
        }
      } catch (error) {
        console.error('Error fetching genders:', error);
      }
    };
    fetchGenders();
  }, []);

  const formatAccountHolderName = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatBranchName = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleInputChange = (eOrOption, fieldName) => {
    if (eOrOption && eOrOption.target) {
      const { name, value } = eOrOption.target;
      if (name === "nomineeName") {
        setPersonalData(prev => ({
          ...prev,
          nomineeDetails: {
            ...prev.nomineeDetails,
            [name]: value
          }
        }));
        console.log('Updated nomineeDetails:', { ...personalData.nomineeDetails, [name]: value });
      } else {
        setPersonalData(prev => ({
          ...prev,
          [name]: value
        }));
        console.log('Updated personalData:', { ...personalData, [name]: value });
      }
      if (name === "employeeName") {
        localStorage.setItem("employeeName", value);
      }
    } else {
      const value = eOrOption ? Number(eOrOption.value) : '';
      if (fieldName === "nomineeRelationship") {
        setPersonalData(prev => ({
          ...prev,
          nomineeDetails: {
            ...prev.nomineeDetails,
            relationship: value
          }
        }));
      } else {
        setPersonalData(prev => ({
          ...prev,
          [fieldName]: value
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({
      ...prev,
      [name]: value
    }));
    let error = '';
    if (name === "accountNumber" && value && !/^\d{9,15}$/.test(value)) {
      error = "Account number must be 9 to 15 digits";
    }
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          employeePhoto: 'Please upload a valid image file (JPEG, PNG)'
        }));
        return;
      }
      const photoUrl = URL.createObjectURL(file);
      setPersonalData(prev => ({
        ...prev,
        photoUrl,
        photoFile: file
      }));
      setErrors(prev => ({
        ...prev,
        employeePhoto: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      alert("Please fill all required fields correctly before submitting.");
      return;
    }

    const fullName = localStorage.getItem("full_name");
    const role = localStorage.getItem("role");
    const employeeId = localStorage.getItem("employeeId");

    const formData = new FormData();
    if (personalData.photoFile) {
      formData.append('file', personalData.photoFile);
    }

    const payload = {
      nameAsPerAadhaar: personalData.nameAsAadhar,
      mobileNumber: personalData.contactNumber,
      genderId: personalData.gender,
      dateOfBirthAsPerAadhaar: personalData.dateOfBirth,
      fatherOrHusbandName: personalData.guardianName,
      relationshipId: personalData.relationship,
      addressDetailsId: personalData.addressId, // Use addressId, fallback to 47
      nomineeName: personalData.nomineeDetails.nomineeName,
      nomineeRelationshipId: personalData.nomineeDetails.relationship,
      nomineePresentAddress: personalData.nomineeAddress,
      isNomineeStayingWithYou: personalData.nomineeDetails.stayingWithInsured === "yes",
      bankDetailsId: bankData.id, // Use bankData.id, fallback to 42
      auditLog: {
        employeeId: employeeId,
        role: role,
        name: fullName
      }
    };

    formData.append('request', JSON.stringify(payload));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/employees/esic/${employeeUniqueId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('ESIC creation successful:', response.data);
      alert('ESIC form submitted successfully!');
      localStorage.removeItem('personalData');
      localStorage.removeItem('bankData');
      setPersonalData({
        id: "",
        surName: "",
        employeeName: "",
        guardianName: "",
        relationship: "",
        nameAsAadhar: "",
        gender: "",
        dateOfBirth: "",
        aadhaarNumber: "",
        contactNumber: "",
        emailAddress: "",
        photoUrl: "",
        photoFile: null,
        addressId: null,
        addresses: {
          presentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          },
          permanentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          }
        },
        nomineeDetails: {
          nomineeName: "",
          relationship: "",
          stayingWithInsured: ""
        },
        nomineeAddress: ""
      });
      setBankData({
        id: null,
        accountHolderName: "",
        accountNumber: "",
        ifscCode: "",
        branchName: ""
      });
    } catch (error) {
      console.error('Error submitting ESIC form:', error);
      alert('Failed to submit ESIC form. Please try again.');
    }
  };

  const currentYear = new Date().getFullYear();
  const minDate = new Date(1965, 0, 1).toISOString().split('T')[0];
  const today = new Date();
  const maxDate = today.toISOString().split('T')[0];
  const dateInputRef = useRef(null);
  const handleDateInputClick = () => {
    dateInputRef.current?.showPicker();
  };

  // const capitalizeFirstLetter = (str) => {
  //   if (!str) return "";
  //   return str
  //     .split(" ")
  //     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  //     .join(" ");
  // };
  const handleClear = () => {
    navigate("/dashboard")
  }

  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (!employeeUniqueId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/personal-info/${employeeUniqueId}`
        );
        const appData = response.data;
        const employeeIDDetails = appData.employeeIDDetails || {};
        const bank = appData.bankDetails || {};
        const address = appData.addresses[0] || {};

        setPersonalData(prev => ({
          ...prev,
          surName: appData.surname || "",
          employeeName: appData.givenName || "",
          jobPosition: appData.employee?.jobPosition || "",
          nameAsAadhar: employeeIDDetails.nameAsPerAadhaar || "",
          gender: appData.gender?.id || "",
          dateOfBirth: appData.dateOfBirth || "",
          contactNumber: appData.contactNumber?.slice(0, 10) || "",
          emailAddress: appData.email || "",
          photoUrl: employeeIDDetails.employeepassportSizeFileName || "",
          photoFile: null,
          addressId: address.id || null,
          addresses: {
            presentAddress: {
              line1: address.presentAddressLine1 || "",
              line2: address.presentAddressLine2 || "",
              block: address.presentBlock || "",
              district: address.presentDistrict?.districtName || "",
              state: address.presentState?.stateName || "",
              country: address.presentCountry?.nationalityName || "",
              postcode: address.presentPostCode || ""
            },
            permanentAddress: {
              line1: address.permanentAddressLine1 || "",
              line2: address.permanentAddressLine2 || "",
              block: address.permanentBlock || "",
              district: address.permanentDistrict?.districtName || "",
              state: address.permanentState?.stateName || "",
              country: address.permanentCountry?.nationalityName || "",
              postcode: address.permanentPostCode || ""
            }
          },
          nomineeDetails: {
            nomineeName: employeeIDDetails.nomineeName || "",
            relationship: employeeIDDetails.nomineeRelationship?.id ? Number(employeeIDDetails.nomineeRelationship.id) : "",
            stayingWithInsured: employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no"
          },
          nomineeAddress: employeeIDDetails.nomineePresentAddress || ""
        }));
        setBankData({
          id: bank.id || null,
          accountHolderName: bank.accountHolderName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          branchName: bank.branchName || ""
        });
        setNomineeStaying(employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no");
      } catch (error) {
        console.error("Error fetching application data:", error);
      }
    };
    fetchApplicationDetails();
  }, [employeeUniqueId]);

  return (
    <React.Fragment>
      <div className="esic-container">
        <Routes>
          <Route path="/registrationnavbar" element={<RegistrationNavbar />} />
        </Routes>
        <div className="esic-box">
          <h1 className="esic-heading">ESIC Form</h1>
          <div className="pendingdetails-heading">
            <span className="highlight-label">Temporary ID :</span> {employeeUniqueId}-{personalData.employeeName} {personalData.surName}-{personalData.jobPosition?.designationName}
          </div>
          <form onSubmit={handleSubmit}>
            <h2 className="esic-heading">Personal Details</h2>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="nameAsAadhar"
                    value={personalData.nameAsAadhar}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        const updatedValue = capitalizeFirstLetter(value);
                        handleInputChange({ target: { name: "nameAsAadhar", value: updatedValue } });
                      }
                    }}
                    placeholder=" "
                    disabled
                    required
                  />
                  <label><span className="required">*</span>Name as per Aadhar</label>
                  {errors.nameAsAadhar && <p className="error">{errors.nameAsAadhar}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="contactNumber"
                    value={personalData.contactNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    disabled
                    required
                    placeholder=" "
                    maxLength="10"
                    pattern="^[0-9]{10}$"
                    title="Please enter a valid 10-digit phone number."
                  />
                  <label><span className="required">*</span>Contact Number</label>
                  {errors.contactNumber && <p className="error">{errors.contactNumber}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className={`esic-floating-label-container-2 ${isFocused.gender || !!personalData.gender ? 'float-label' : ''}`}>
                  <Select
                    name="gender"
                    options={genders}
                    value={genders.find(opt => opt.value === personalData.gender) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'gender')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, gender: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, gender: false }))}
                    placeholder=" "
                    isClearable
                    isDisabled
                    styles={{
                      menuPortal: base => ({
                        ...base,
                        zIndex: 9999,
                        position: 'absolute',
                        top: 'auto',
                        bottom: '100%'
                      }),
                      menu: base => ({
                        ...base,
                        marginTop: '5px',
                        zIndex: 9999
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        padding: '1px',
                        backgroundColor: state.isDisabled ? '#e4e4e4' : 'white',
                        borderColor: state.isDisabled ? '#ccc' : provided.borderColor,
                        cursor: state.isDisabled ? 'not-allowed' : 'default',
                        opacity: 1,
                        pointerEvents: state.isDisabled ? 'auto' : 'auto'
                      })
                    }}
                  />
                  <label><span className="required">*</span>Gender</label>
                  {errors.gender && <p className="error">{errors.gender}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className={`esic-floating-label-container-2 ${isFocused.dateOfBirth || !!personalData.dateOfBirth ? 'float-label' : ''}`}>
                  <DatePicker
                    selected={personalData.dateOfBirth ? new Date(personalData.dateOfBirth) : null}
                    onChange={(date) => {
                      const formatted = date ? date.toISOString().split('T')[0] : '';
                      handleInputChange({ target: { name: 'dateOfBirth', value: formatted } });
                    }}
                    dateFormat="dd-MM-yyyy"
                    placeholderText=""
                    className="did-floating-input"
                    onFocus={() => setIsFocused(prev => ({ ...prev, dateOfBirth: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, dateOfBirth: false }))}
                    ref={dateInputRef}
                    minDate={new Date(minDate)}
                    maxDate={new Date(maxDate)}
                    disabled
                  />
                  <label><span className="required">*</span>Date of Birth</label>
                  {errors.dateOfBirth && <p className="error">{errors.dateOfBirth}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="guardianName"
                    value={personalData.guardianName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder=" "
                    required
                  />
                  <label><span className="required">*</span>Father's Name / Husband Name</label>
                  {errors.guardianName && <p className="error">{errors.guardianName}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className={`esic-floating-label-container-2 ${isFocused.relationship || !!personalData.relationship ? 'float-label' : ''}`}>
                  <Select
                    name="relationship"
                    options={relationship}
                    value={relationship.find(opt => opt.value === personalData.relationship) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'relationship')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, relationship: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, relationship: false }))}
                    placeholder=" "
                    isClearable
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999, position: 'absolute', top: 'auto', bottom: '100%' }),
                      menu: base => ({ ...base, marginTop: '5px', zIndex: 9999 }),
                      control: provided => ({ ...provided, padding: '1px' }),
                      valueContainer: provided => ({ ...provided, padding: '3px 6px' })
                    }}
                  />
                  <label><span className="required">*</span>Relationship</label>
                  {errors.relationship && <p className="error">{errors.relationship}</p>}
                </div>
              </div>
            </div>

            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="permanentAddress"
                    value={`${personalData.addresses.permanentAddress.line1}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Permanent Address Line 1</label>
                  {errors.permanentAddress && <p className="error">{errors.permanentAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="presentAddress"
                    value={`${personalData.addresses.presentAddress.line1}`}

                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Present Address</label>
                  {errors.presentAddress && <p className="error">{errors.presentAddress}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="permanentAddress"
                    value={`${personalData.addresses.permanentAddress.line2}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Permanent Address Line 2</label>
                  {errors.permanentAddress && <p className="error">{errors.permanentAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="presentAddress"
                    value={`${personalData.addresses.presentAddress.line2}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Present Address Line 2</label>
                  {errors.presentAddress && <p className="error">{errors.presentAddress}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="permanentAddress"
                    value={`${personalData.addresses.permanentAddress.block}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Permanent Address Lane 3</label>
                  {errors.permanentAddress && <p className="error">{errors.permanentAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="presentAddress"
                    value={` ${personalData.addresses.presentAddress.block}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Present Address Lane 3</label>
                  {errors.presentAddress && <p className="error">{errors.presentAddress}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="permanentAddress"
                    value={`${personalData.addresses.permanentAddress.district}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Permanent District</label>
                  {errors.permanentAddress && <p className="error">{errors.permanentAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="presentAddress"
                    value={`${personalData.addresses.presentAddress.district}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Present District</label>
                  {errors.presentAddress && <p className="error">{errors.presentAddress}</p>}
                </div>
              </div>
            </div><div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="permanentAddress"
                    value={`${personalData.addresses.permanentAddress.postcode}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Permanent Pincode</label>
                  {errors.permanentAddress && <p className="error">{errors.permanentAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="presentAddress"
                    value={`${personalData.addresses.presentAddress.postcode}`}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}

                    required
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Present Pincode</label>
                  {errors.presentAddress && <p className="error">{errors.presentAddress}</p>}
                </div>
              </div>
            </div>
            <h2 className="esic-heading">Nominee Details</h2>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeName"
                    value={personalData.nomineeDetails.nomineeName}
                    onChange={(e) => {
                      const formatted = capitalizeFirstLetter(e.target.value);
                      handleInputChange({ target: { name: "nomineeName", value: formatted } });
                    }}
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Nominee Name</label>
                  {errors.nomineeName && <p className="error">{errors.nomineeName}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className={`esic-floating-label-container-2 ${isFocused.nomineeRelationship || !!personalData.nomineeDetails.relationship ? 'float-label' : ''}`}>
                  <Select
                    name="nomineeRelationship"
                    options={relationship}
                    value={relationship.find((opt) => opt.value === personalData.nomineeDetails.relationship) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'nomineeRelationship')}
                    onFocus={() => setIsFocused((prev) => ({ ...prev, nomineeRelationship: true }))}
                    onBlur={() => setIsFocused((prev) => ({ ...prev, nomineeRelationship: false }))}
                    placeholder=" "
                    isClearable
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999, position: 'absolute', top: 'auto', bottom: '100%' }),
                      menu: (base) => ({ ...base, marginTop: '5px', zIndex: 9999 }),
                      control: (provided) => ({ ...provided, padding: '1px' }),
                      valueContainer: (provided) => ({ ...provided, padding: '3px 6px' })
                    }}
                  />
                  <label><span className="required">*</span>Relationship with Nominee</label>
                  {errors.nomineeRelationship && <p className="error">{errors.nomineeRelationship}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeAddress"
                    value={personalData.nomineeAddress}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Nominee Address</label>
                  {errors.nomineeAddress && <p className="error">{errors.nomineeAddress}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <label className="label-text-color"><span className="required">*</span>Is Nominee Staying With You:</label> &nbsp;&nbsp;
                <label>
                  <input
                    type="radio"
                    name="nomineeStaying"
                    value="yes"
                    checked={nomineeStaying === 'yes'}
                    onChange={(e) => {
                      setNomineeStaying(e.target.value);
                      setPersonalData(prev => ({
                        ...prev,
                        nomineeDetails: {
                          ...prev.nomineeDetails,
                          stayingWithInsured: e.target.value
                        }
                      }));
                    }}
                  /> Yes
                </label>
                <label style={{ marginLeft: '1em' }}>
                  <input
                    type="radio"
                    name="nomineeStaying"
                    value="no"
                    checked={nomineeStaying === 'no'}
                    onChange={(e) => {
                      setNomineeStaying(e.target.value);
                      setPersonalData(prev => ({
                        ...prev,
                        nomineeDetails: {
                          ...prev.nomineeDetails,
                          stayingWithInsured: e.target.value
                        }
                      }));
                    }}
                  /> No
                </label>
              </div>
            </div>
            <h2 className="esic-heading">Bank Details</h2>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="accountHolderName"
                    value={bankData.accountHolderName}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        value = formatAccountHolderName(value);
                        handleChange({ target: { name: "accountHolderName", value } });
                      }
                    }}
                    required
                    placeholder=" "
                    disabled
                  />
                  <label><span className="required">*</span>Name as per Bank Account</label>
                  {errors.accountHolderName && <p className="error">{errors.accountHolderName}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankData.accountNumber}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^\d{0,15}$/.test(value)) {
                        handleAccountChange({ target: { name: "accountNumber", value } });
                      } else {
                        setErrors(prev => ({
                          ...prev,
                          accountNumber: "Only numbers allowed, max 15 digits"
                        }));
                      }
                    }}
                    maxLength={15}
                    required
                    placeholder=" "
                    disabled
                  />
                  <label><span className="required">*</span>Account Number</label>
                  {errors.accountNumber && <p className="error">{errors.accountNumber}</p>}
                </div>
              </div>
            </div>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="ifscCode"
                    placeholder=""
                    value={bankData.ifscCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      handleChange({ target: { name: "ifscCode", value } });
                      let error = '';
                      if (value && value.length === 11 && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
                        error = '4 letters (bank) + 0 + 6 alphanumeric (branch)';
                      }
                      setErrors(prev => ({ ...prev, ifscCode: error }));
                    }}
                    maxLength={11}
                    pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                    title="The IFSC code is an 11-character code: The first 4 characters are uppercase letters (bank code), followed by a '0', and the last 6 characters can be alphanumeric (branch code)."
                    style={{ borderColor: errors.ifscCode ? 'red' : undefined }}
                    disabled
                  />
                  <label><span className="required">*</span>IFSC Code</label>
                  {errors.ifscCode && <p className="error">{errors.ifscCode}</p>}
                </div>
              </div>
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  <input
                    type="text"
                    name="branchName"
                    value={bankData.branchName}
                    onChange={(e) => {
                      const formatted = formatBranchName(e.target.value);
                      handleChange({ target: { name: "branchName", value: formatted } });
                    }}
                    placeholder=""
                    disabled
                  />
                  <label>Branch Name</label>
                  {errors.branchName && <p className="error">{errors.branchName}</p>}
                </div>
              </div>
            </div>
            <h2 className="esic-heading">Photo</h2>
            <div className="esic-maincontainer">
              <div className="esic-container-part">
                <div className="esic-floating-label-container-1">
                  {personalData.photoUrl && (
                    <div style={{ marginBottom: '10px' }}>
                      <img
                        src={personalData.photoUrl}
                        alt="Employee Passport Photo"
                        style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          setErrors(prev => ({
                            ...prev,
                            employeePhoto: 'Failed to load image'
                          }));
                        }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    name="employeePhoto"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoChange}
                  />
                  <label>Upload Passport Photo</label>
                  {errors.employeePhoto && <p className="error">{errors.employeePhoto}</p>}
                </div>
              </div>
            </div>
            <div className="esic-btn-div">
              <button type="submit" disabled={!isFormValid} className="esic-btn">
                Submit ESIC Form
              </button>
              <button type="button" className="esic-btn-can" onClick={handleClear}>
                cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ESICForm;